import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../auth/auth-context';
import { Tag } from '../components/Tag';
import { DisparoDeJornadas, type ModoDeDisparo } from '../components/DisparoDeJornadas';
import { Carregando, ErroBox, Modal, ModalRodape, PageHeader, Vazio } from '../components/ui';

interface Agendamento {
  eventId: string;
  eventName: string;
  scheduledFor: string;
  timezone: string;
  templateId: string | null;
  templateName: string | null;
  pessoas: number;
  cancelados: number;
  despachados: number;
}

interface Resposta {
  data: Agendamento[];
  /** O servidor parou no teto e existe mais coisa marcada do que a lista mostra. */
  truncado: boolean;
}

/**
 * O que vai disparar, de todos os eventos.
 *
 * A jornada é sempre de UM evento e é lá que ela se opera. O que não existia
 * era a pergunta que atravessa: "o que sai amanhã?". Sem esta tela, respondê-la
 * exigia abrir evento por evento — e é a pergunta que se faz justamente quando
 * não dá tempo de abrir evento por evento.
 */
export function Agendamentos() {
  const { pode } = useAuth();
  const cliente = useQueryClient();

  const [incluirEncerrados, setIncluirEncerrados] = useState(false);
  const [cancelando, setCancelando] = useState<Agendamento | null>(null);
  const [disparo, setDisparo] = useState<ModoDeDisparo | null>(null);

  const consulta = useQuery({
    queryKey: ['agendamentos', incluirEncerrados],
    queryFn: () => api<Resposta>(`/schedules?incluirEncerrados=${incluirEncerrados}`),
  });

  const cancelar = useMutation({
    mutationFn: (alvo: Agendamento) =>
      api<{ cancelados: number }>('/schedules/cancelar', {
        method: 'POST',
        body: { eventId: alvo.eventId, quando: alvo.scheduledFor },
      }),
    onSuccess: () => {
      cliente.invalidateQueries({ queryKey: ['agendamentos'] });
      cliente.invalidateQueries({ queryKey: ['jornadas'] });
      setCancelando(null);
    },
  });

  return (
    <>
      <PageHeader
        titulo="Agendamentos"
        subtitulo="Disparos marcados, de todos os eventos. No horário, quem envia é a fila — ninguém precisa estar na tela."
        acoes={
          <>
            <label className="filtro-inline">
              <input
                type="checkbox"
                checked={incluirEncerrados}
                onChange={(e) => setIncluirEncerrados(e.target.checked)}
              />
              Mostrar disparados e cancelados
            </label>
            {/* As mesmas portas da tela de Jornadas do evento — aqui a janela
                pergunta qual evento. Ver `DisparoDeJornadas`. */}
            {pode('journeys.schedule') && (
              <button className="btn btn-secondary" onClick={() => setDisparo('agendar')}>
                <i className="ti ti-clock-play" /> Agendar
              </button>
            )}
            {pode('journeys.start') && (
              <button className="btn btn-primary" onClick={() => setDisparo('iniciar')}>
                <i className="ti ti-send" /> Iniciar jornadas
              </button>
            )}
          </>
        }
      />

      {consulta.isPending && <Carregando />}
      {consulta.isError && <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />}

      {consulta.data && consulta.data.truncado && (
        <div className="aviso" style={{ marginBottom: 16 }}>
          <i className="ti ti-alert-triangle" />
          <span>
            Há mais agendamentos do que esta lista mostra. Os mais próximos aparecem primeiro.
          </span>
        </div>
      )}

      {consulta.data &&
        (consulta.data.data.length === 0 ? (
          <Vazio
            icone="ti-clock-play"
            titulo="Nada marcado"
            descricao={
              incluirEncerrados
                ? 'Nenhuma jornada foi agendada ainda.'
                : 'Nenhum disparo pendente. Use "Agendar", acima, para marcar o convite de um evento.'
            }
          />
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <div className="tabela-envolvente">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Quando</th>
                    <th>Evento</th>
                    <th>Template</th>
                    <th>Pessoas</th>
                    <th>Situação</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {consulta.data.data.map((a) => {
                    const pendentes = a.pessoas - a.cancelados - a.despachados;

                    return (
                      <tr key={`${a.eventId}-${a.scheduledFor}-${a.templateId ?? ''}`}>
                        <td>
                          <div className="celula-principal">{formatarQuando(a.scheduledFor)}</div>
                          <div className="celula-secundaria">{a.timezone}</div>
                        </td>
                        <td>
                          <Link to={`/eventos/${a.eventId}`} className="celula-principal">
                            {a.eventName}
                          </Link>
                        </td>
                        <td>
                          {a.templateName ?? (
                            <span style={{ color: 'var(--text-dim)' }}>—</span>
                          )}
                        </td>
                        <td style={{ fontVariantNumeric: 'tabular-nums' }}>{a.pessoas}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            {pendentes > 0 && <Tag type="info">{pendentes} pendente(s)</Tag>}
                            {a.despachados > 0 && <Tag type="success">{a.despachados} enviada(s)</Tag>}
                            {a.cancelados > 0 && <Tag type="muted">{a.cancelados} cancelada(s)</Tag>}
                          </div>
                        </td>
                        <td>
                          <div className="acoes-linha">
                            {pendentes > 0 && pode('journeys.cancel') && (
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => setCancelando(a)}
                              >
                                <i className="ti ti-x" /> Cancelar
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ))}

      {disparo && <DisparoDeJornadas modo={disparo} aoFechar={() => setDisparo(null)} />}

      {cancelando && (
        <Modal
          titulo="Cancelar agendamento"
          aoFechar={() => setCancelando(null)}
          largura={460}
        >
          {cancelar.isError && (
            <div className="form-erro">{(cancelar.error as Error).message}</div>
          )}

          <p style={{ fontSize: 13, lineHeight: 1.6, color: 'var(--text-muted)' }}>
            O disparo de <strong style={{ color: 'var(--text)' }}>{cancelando.eventName}</strong>,
            marcado para <strong style={{ color: 'var(--text)' }}>{formatarQuando(cancelando.scheduledFor)}</strong>,
            não acontece.{' '}
            {cancelando.despachados > 0 && (
              <>
                As <strong style={{ color: 'var(--text)' }}>{cancelando.despachados}</strong> que já
                saíram continuam de pé — mensagem enviada não se recolhe.
              </>
            )}
          </p>

          <ModalRodape>
            <button className="btn btn-secondary" onClick={() => setCancelando(null)}>
              Manter
            </button>
            <button
              className="btn btn-primary"
              disabled={cancelar.isPending}
              onClick={() => cancelar.mutate(cancelando)}
            >
              {cancelar.isPending ? 'Cancelando…' : 'Cancelar disparo'}
            </button>
          </ModalRodape>
        </Modal>
      )}
    </>
  );
}

/** A data é do fuso do EVENTO — a coluna ao lado diz qual é. */
function formatarQuando(iso: string) {
  return new Date(iso).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
